import React, { useState, useEffect } from 'react';
import { Card, Statistic, Button, Space, Spin, Select, Input, message } from 'antd';
import { SettingOutlined, ReloadOutlined, ArrowUpOutlined, ArrowDownOutlined } from '@ant-design/icons';
import ReportService from '../../services/reportBuilder.service';

const { Option } = Select;

const MetricWidget = ({
  widget,
  onEdit,
  onRefresh,
  isEditing = false,
  style = {}
}) => {
  const [data, setData] = useState([]);
  const [loading, setLoading] = useState(false);
  const [metricField, setMetricField] = useState(widget?.config?.metricField);
  const [aggregation, setAggregation] = useState(widget?.config?.aggregation || 'count');
  const [title, setTitle] = useState(widget?.config?.title || widget?.name || 'Metric');
  const [previousValue, setPreviousValue] = useState(null);

  useEffect(() => {
    if (widget?.config?.dataSource) {
      loadWidgetData();
    }
  }, [widget]);

  const loadWidgetData = async () => {
    if (!widget?.config?.dataSource) return;

    setLoading(true);
    try {
      const result = await ReportService.executeSavedSearch(
        widget.config.entityType || 'items',
        widget.config.dataSource,
        1000,
        0
      );

      if (result && result.data) {
        setData(result.data);
        calculateMetric(result.data);
      }
    } catch (error) {
      console.error('Error loading widget data:', error);
      message.error('Failed to load widget data');
    } finally {
      setLoading(false);
    }
  };

  const calculateMetric = (data) => {
    if (!data || data.length === 0 || !metricField) return;

    let value = 0;

    switch (aggregation) {
      case 'count':
        value = data.length;
        break;
      case 'sum':
        value = data.reduce((sum, item) => {
          const itemValue = item[metricField];
          return sum + (typeof itemValue === 'number' ? itemValue : parseFloat(itemValue) || 0);
        }, 0);
        break;
      case 'avg':
        const sum = data.reduce((total, item) => {
          const itemValue = item[metricField];
          return total + (typeof itemValue === 'number' ? itemValue : parseFloat(itemValue) || 0);
        }, 0);
        value = data.length > 0 ? sum / data.length : 0;
        break;
      case 'min':
        value = Math.min(...data.map(item => {
          const itemValue = item[metricField];
          return typeof itemValue === 'number' ? itemValue : parseFloat(itemValue) || 0;
        }));
        break;
      case 'max':
        value = Math.max(...data.map(item => {
          const itemValue = item[metricField];
          return typeof itemValue === 'number' ? itemValue : parseFloat(itemValue) || 0;
        }));
        break;
      default:
        value = data.length;
    }

    // Store current value for comparison
    setPreviousValue(widget?.config?.currentValue);
    updateWidgetValue(value);
  };

  const updateWidgetValue = (value) => {
    if (onEdit) {
      onEdit(widget.id, {
        ...widget,
        config: {
          ...widget.config,
          currentValue: value,
          lastUpdated: new Date().toISOString()
        }
      });
    }
  };

  const handleRefresh = () => {
    loadWidgetData();
    onRefresh && onRefresh(widget.id);
  };

  const handleMetricFieldChange = (value) => {
    setMetricField(value);
    if (onEdit) {
      onEdit(widget.id, {
        ...widget,
        config: {
          ...widget.config,
          metricField: value
        }
      });
    }
  };

  const handleAggregationChange = (value) => {
    setAggregation(value);
    if (onEdit) {
      onEdit(widget.id, {
        ...widget,
        config: {
          ...widget.config,
          aggregation: value
        }
      });
    }
  };

  const handleTitleChange = (e) => {
    const newTitle = e.target.value;
    setTitle(newTitle);
    if (onEdit) {
      onEdit(widget.id, {
        ...widget,
        config: {
          ...widget.config,
          title: newTitle
        }
      });
    }
  };

  const currentValue = widget?.config?.currentValue || 0;
  const prevValue = previousValue !== null ? previousValue : currentValue;
  const percentChange = prevValue !== 0 ? ((currentValue - prevValue) / prevValue) * 100 : 0;

  const getValueColor = () => {
    if (percentChange > 0) return '#3f8600';
    if (percentChange < 0) return '#cf1322';
    return '#1890ff';
  };

  const getArrowIcon = () => {
    if (percentChange > 0) return <ArrowUpOutlined />;
    if (percentChange < 0) return <ArrowDownOutlined />;
    return null;
  };

  return (
    <Card
      style={{
        height: widget?.config?.size?.height || 200,
        ...style
      }}
      bodyStyle={{ height: '100%', display: 'flex', alignItems: 'center', justifyContent: 'center' }}
    >
      {isEditing ? (
        <Space direction="vertical" style={{ width: '100%', padding: '16px' }}>
          <Input
            value={title}
            onChange={handleTitleChange}
            placeholder="Widget title"
          />

          <Select
            value={metricField}
            onChange={handleMetricFieldChange}
            style={{ width: '100%' }}
            placeholder="Select metric field"
          >
            {data.length > 0 && Object.keys(data[0]).map(field => (
              <Option key={field} value={field}>
                {field.replace(/([A-Z])/g, ' $1').replace(/^./, str => str.toUpperCase())}
              </Option>
            ))}
          </Select>

          <Select
            value={aggregation}
            onChange={handleAggregationChange}
            style={{ width: '100%' }}
            placeholder="Select aggregation"
          >
            <Option value="count">Count</Option>
            <Option value="sum">Sum</Option>
            <Option value="avg">Average</Option>
            <Option value="min">Minimum</Option>
            <Option value="max">Maximum</Option>
          </Select>
        </Space>
      ) : (
        <Spin spinning={loading}>
          <Statistic
            title={
              <Space>
                <span>{title}</span>
                <Button
                  type="text"
                  icon={<ReloadOutlined />}
                  onClick={handleRefresh}
                  loading={loading}
                  size="small"
                />
                <Button
                  type="text"
                  icon={<SettingOutlined />}
                  onClick={() => onEdit && onEdit(widget.id)}
                  size="small"
                />
              </Space>
            }
            value={currentValue}
            valueStyle={{ color: getValueColor(), fontSize: '32px' }}
            suffix={aggregation === 'count' ? '' : (aggregation === 'sum' || aggregation === 'avg' ? '$' : '')}
            precision={aggregation === 'count' ? 0 : 2}
          />
          {previousValue !== null && percentChange !== 0 && (
            <div style={{ marginTop: 8, fontSize: '14px', color: getValueColor() }}>
              {getArrowIcon()} {Math.abs(percentChange).toFixed(1)}% from last update
            </div>
          )}
        </Spin>
      )}
    </Card>
  );
};

export default MetricWidget;